// Signed-in accounts on the client side.
//
// Sign-in happens on a SEPARATE origin (the auth service) because this app runs under
// cross-origin isolation, which breaks Google's sign-in. We full-page redirect to the
// auth origin, it does the whole Google dance, and it redirects back here with a signed
// session token in the URL fragment. We store {token, profile} locally and send the token
// as a bearer on the account endpoints (cloud backup, reviews); the server verifies it
// against a secret it shares with the auth service.

import { collectSave, applySave } from "../shared/backup.js";
import { noteSafe } from "../shared/health-gate.js";

const LS_SESSION = "revault:account";
const listeners = new Set();
let cfg = null; // { enabled, authOrigin }, fetched once

function lsGet(k) { try { return localStorage.getItem(k) || ""; } catch (e) { void e; return ""; } }
function lsSet(k, v) { try { localStorage.setItem(k, v); } catch (e) { void e; } }
function lsDel(k) { try { localStorage.removeItem(k); } catch (e) { void e; } }

export function getSession() {
	try { const j = JSON.parse(lsGet(LS_SESSION)); return j && j.token ? j : null; } catch (e) { void e; return null; }
}
export function setSession(sess) { lsSet(LS_SESSION, JSON.stringify(sess)); emit(); }
export function signOut() { lsDel(LS_SESSION); emit(); }
export function isSignedIn() { return !!getSession(); }
export function profile() { const s = getSession(); return s ? s.profile : null; }

export function onAccountChange(fn) { listeners.add(fn); return () => listeners.delete(fn); }
function emit() { for (const fn of listeners) { try { fn(getSession()); } catch (e) { void e; } } }
// Another tab signed in, signed out or got a renewed token (or a sign-in finished in the app
// window, for a Settings tab). getSession() always reads storage, so requests already use the
// new token; this just lets the UI here know.
if (typeof window !== "undefined") window.addEventListener("storage", (e) => { if (e.key === LS_SESSION) emit(); });

// The claims part of the token (unverified; only used to decide when to check in).
function tokenClaims(token) {
	try {
		return JSON.parse(atob(String(token).split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
	} catch (e) { void e; return null; }
}

export async function accountConfig() {
	if (cfg) return cfg;
	try { cfg = await (await fetch("/account/config", { cache: "no-store" })).json(); }
	catch (e) { void e; cfg = { enabled: false, authOrigin: "" }; }
	return cfg;
}

// Attach the bearer token to a request. Returns the Response, or throws on 401 so
// callers can prompt a re-sign-in.
export async function authedFetch(url, opts = {}) {
	const sess = getSession();
	if (!sess) throw new Error("not signed in");
	const headers = { ...(opts.headers || {}), Authorization: "Bearer " + sess.token };
	const res = await fetch(url, { ...opts, headers, cache: "no-store" });
	if (res.status === 401) {
		// Only sign out if the token we sent is still the stored one: another tab may have
		// swapped in a renewed token while this request was in flight.
		const cur = getSession();
		if (!cur || cur.token === sess.token) signOut();
		throw new Error("session expired");
	}
	return res;
}

// Sign-in happens on the auth origin, never inside this page: this app runs with COOP same-origin
// (cross-origin isolation, which CheerpX Linux machines need), which rules out a popup, so the TOP
// window navigates there and comes back with the token in the URL fragment (consumeRedirectToken).
// Discord through a Re:Vault tab and Ep3 codes are the exception: the sheet
// (src/client/signin/sheet.js) stays here and gets the token from the auth origin's CORS answer.
//
// BYOD: any domain this server serves can sign in, not just the canonical few. We ask our own
// backend for a short-lived "origin ticket" that vouches for the current origin; the auth service
// accepts that in place of its allowlist.
export async function originTicket() {
	try {
		const t = await fetch("/account/origin-ticket", { cache: "no-store" });
		if (t.ok) return (await t.json()).ticket || "";
	} catch (e) { void e; }
	return "";
}
async function buildStartUrl(method = "google") {
	const c = await accountConfig();
	if (!c.enabled || !c.authOrigin) throw new Error("Sign-in isn't available right now.");
	let url = c.authOrigin + "/auth/" + encodeURIComponent(method) + "/start?origin=" + encodeURIComponent(location.origin);
	const ticket = await originTicket();
	if (ticket) url += "&ticket=" + encodeURIComponent(ticket);
	return { url, authOrigin: c.authOrigin };
}

// Navigate the top window to a method's start page. Settings and other first-party pages run in
// sandboxed iframes that can't navigate the top frame, so from a frame we ask the top to do it.
export async function startRedirect(method = "google", isCurrent = () => true) {
	const { url } = await buildStartUrl(method);
	if (!isCurrent()) return;
	if (window.top && window !== window.top) {
		window.top.postMessage({ type: "revault-signin-nav", url }, location.origin);
		return new Promise(() => {});
	}
	location.href = url;
	return new Promise(() => {});
}

// Open the sign-in sheet. The top Re:Vault window owns it (it holds Discord and Ep3 flows while
// the student is in another tab); a framed page asks the top. A top-level page that isn't the app
// (Study Hall opened on its own) goes to the app, which opens the sheet on load. `opts.intent` is
// "signin" or "link" (with `opts.method` to add one way directly).
let sheetOpener = null;
export function setSignInSheet(fn) { sheetOpener = fn; }
export async function signIn(opts = {}) {
	const intent = opts.intent === "link" ? "link" : "signin";
	const method = typeof opts.method === "string" ? opts.method : "";
	if (window.top && window !== window.top) {
		window.top.postMessage({ type: "revault-signin-open", intent, method }, location.origin);
		return new Promise(() => {});
	}
	if (sheetOpener) { sheetOpener({ intent, method }); return new Promise(() => {}); }
	location.href = "/#revault-signin";
	return new Promise(() => {});
}

// Installed by the TOP app only: navigate on a signin request relayed from a same-origin frame
// (checked against the configured auth origin), and open the sheet when a first-party page asks.
// Adding a way to sign in is honoured only from the Settings tab: it acts on this account.
export function initTopBridge() {
	if (window.top && window !== window.top) return;
	window.addEventListener("message", (e) => {
		if (e.origin !== location.origin) return;
		const d = e.data;
		if (!d) return;
		if (d.type === "revault-signin-nav" && typeof d.url === "string") {
			accountConfig().then((c) => { if (c.authOrigin && d.url.indexOf(c.authOrigin + "/") === 0) location.href = d.url; });
			return;
		}
		if (d.type === "revault-signin-open" && sheetOpener) {
			let fromSettings = false;
			try { fromSettings = !!e.source && e.source.parent === window && e.source.location.pathname === "/settings"; } catch (err) { void err; }
			const link = d.intent === "link";
			if (link && !fromSettings) return;
			sheetOpener({ intent: link ? "link" : "signin", method: typeof d.method === "string" ? d.method : "" });
		}
	});
}

// Redirect fallback: some environments block popups, so the auth page can bounce back
// to us with the token in the URL fragment. Consume it on load.
export function consumeRedirectToken() {
	try {
		// The inline head script (shared/invite-early.js) has usually taken it out of the address already.
		let hash = location.hash;
		try { if (typeof window.__rvAuthHash === "string") hash = window.__rvAuthHash; delete window.__rvAuthHash; } catch (e) { void e; }
		const m = hash.match(/[#&]revault-auth=([^&]+)/);
		if (m) {
			const token = decodeURIComponent(m[1]);
			// profile isn't in the fragment; a follow-up /account/me fills it in.
			setSession({ token, profile: {} });
			if (/[#&]revault-auth=/.test(location.hash)) history.replaceState(null, "", location.pathname + location.search);
			refreshProfile();
		}
		const pend = lsGet("revault:auth:pending");
		if (pend) { lsDel("revault:auth:pending"); const d = JSON.parse(pend); if (d && d.token) setSession({ token: d.token, profile: d.profile || {} }); }
	} catch (e) { void e; }
}

// Sign-in started inside Re:Vault OS: the desktop navigated the whole tab to the auth
// origin (a framed page can't), which returns to "/" with the token. The desktop left
// {path: "/os/", at} in localStorage so we can send the person back to it. Call right
// after consumeRedirectToken(). Only the exact path "/os/" is honoured, only within 10
// minutes, only once a session exists, and only in the top window (never nest the OS).
const LS_AUTH_RETURN = "revault:auth:return";
const AUTH_RETURN_MS = 10 * 60 * 1000;
export function consumeAuthReturn() {
	try {
		if (window.top && window !== window.top) return false;
		const raw = lsGet(LS_AUTH_RETURN);
		if (!raw) return false;
		let d = null;
		try { d = JSON.parse(raw); } catch (e) { void e; }
		const at = d && Number(d.at);
		const age = Date.now() - at;
		const fresh = !!d && d.path === "/os/" && Number.isFinite(at) && age >= 0 && age <= AUTH_RETURN_MS;
		// A stale or malformed marker is dropped; a fresh one waits for the session.
		if (!fresh) { lsDel(LS_AUTH_RETURN); return false; }
		if (!getSession()) return false;
		lsDel(LS_AUTH_RETURN);
		location.replace("/os/");
		return true;
	} catch (e) { void e; return false; }
}

// Fill in (or refresh) the profile from the server for the current token.
export async function refreshProfile() {
	const sess = getSession(); if (!sess) return null;
	try {
		const res = await authedFetch("/account/me");
		if (!res.ok) return null;
		const me = await res.json();
		// Signed out in another tab while this was in flight: don't bring the session back.
		const cur = getSession();
		if (!cur) return null;
		// The server sends a fresh token when the current one is a day old (sliding sessions).
		// It replaces the old one under the same key, so every tab and Study Hall pick it up.
		const renewed = typeof me.session === "string" && me.session.split(".").length === 3 ? me.session : "";
		// Another tab signed in again (maybe as someone else) meanwhile: leave its session alone.
		if (cur.token !== sess.token && cur.token !== renewed) { delete me.session; return me; }
		const token = renewed || cur.token;
		// No email in the stored profile (the server stopped sending it): this origin is shared
		// with every proxied page, so it keeps only what the UI shows.
		setSession({ token, profile: { sub: me.sub, name: me.name, picture: (cur.profile && cur.profile.picture) || "" }, backupAt: me.backupAt || 0 });
		delete me.session;
		return me;
	} catch (e) { void e; return null; }
}

// A tab left open for days still renews: check in when the token is a day old. The server
// decides whether to renew; this only avoids calling it when it surely won't.
const RENEW_CHECK_S = 24 * 60 * 60;
const RENEW_POLL_MS = 60 * 60 * 1000;
export function renewDue(sess = getSession(), nowMs = Date.now()) {
	const c = sess && tokenClaims(sess.token);
	if (!c || !Number.isFinite(c.iat)) return false;
	if (Number.isFinite(c.exp) && c.exp * 1000 <= nowMs) return false;
	return nowMs / 1000 - c.iat >= RENEW_CHECK_S;
}
let keepTimer = 0;
export function keepSessionFresh() {
	if (keepTimer) return;
	// At most one check-in per 10 minutes, so a token the server won't renew (an old one
	// without an issuer, say) doesn't turn every tab switch into a request.
	let lastTick = 0;
	const tick = () => {
		if (!renewDue() || Date.now() - lastTick < 10 * 60 * 1000) return;
		lastTick = Date.now();
		refreshProfile();
	};
	keepTimer = setInterval(tick, RENEW_POLL_MS);
	try { document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") tick(); }); } catch (e) { void e; }
}

// --- cloud backup ------------------------------------------------------------------
// Push this device's whole save blob to the cloud.
export async function syncUp() {
	const res = await authedFetch("/account/backup", {
		method: "PUT", headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ blob: collectSave({ cloud: true }) }),
	});
	if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.reason || "Backup failed."); }
	noteSafe("cloud");
	return res.json();
}
// --- leaderboard ------------------------------------------------------------------
// The public board is readable by anyone (handle + review count only).
export async function getLeaderboard() {
	try { const r = await fetch("/leaderboard/reviewers", { cache: "no-store" }); return r.ok ? (await r.json()).board || [] : []; }
	catch (e) { void e; return []; }
}
// The signed-in user's own standing + current opt-in handle.
export async function getStanding() {
	const res = await authedFetch("/account/handle");
	if (!res.ok) throw new Error("Couldn’t load your standing.");
	return res.json();
}
// Set (or clear, with "") the public leaderboard handle.
export async function setHandle(handle) {
	const res = await authedFetch("/account/handle", {
		method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ handle }),
	});
	const j = await res.json().catch(() => ({}));
	if (!res.ok) throw new Error(j.reason || "That name couldn’t be saved.");
	return j;
}

// The cloud backup's save blob, without applying it.
export async function fetchBackup() {
	const res = await authedFetch("/account/backup");
	if (res.status === 404) throw new Error("No cloud backup found yet. Back up first.");
	if (!res.ok) throw new Error("Restore failed.");
	return (await res.json()).blob;
}
// Pull the cloud backup and apply it to this device.
export async function syncDown() {
	const blob = await fetchBackup();
	applySave(blob, { cloud: true });
	await noteSafe("restore");
	return true;
}
